import React, { useState, useEffect } from 'react';
import SafetyBadge from './SafetyBadge';
import CategoryTag from './CategoryTag';

// ─── Query derivation ──────────────────────────────────────────────────────────
// Returns a Pexels-effective search string for the product, or null if
// the category should always use the icon-tile fallback (Food & Pantry).
// Keywords are ordered longest-first so more-specific matches win.

const TYPE_QUERIES = [
  // ── Personal Care ───────────────────────────────────────────────────────────
  [['bubble bath'],                            'bubble bath bathroom'],
  [['dry shampoo'],                            'dry shampoo hair product bottle'],
  [['hair oil', 'argan oil', 'hair serum'],   'hair oil bottle'],
  [['shampoo', 'hair wash'],                   'shampoo bottle bathroom'],
  [['conditioner', 'hair conditioner'],        'hair conditioner bottle bathroom'],
  [['body wash', 'shower gel', 'bath gel'],   'body wash shower bottle'],
  [['face wash', 'facial cleanser', 'face cleanser'], 'face wash skincare bottle'],
  [['face cream', 'day cream', 'night cream'], 'face cream skincare jar'],
  [['moisturizer', 'face lotion'],             'moisturizer skincare cream'],
  [['serum', 'face serum'],                    'face serum skincare dropper bottle'],
  [['toner', 'face toner'],                    'skincare toner bottle'],
  [['eye cream', 'eye serum', 'eye gel'],      'eye cream skincare'],
  [['sunscreen', 'sun cream', 'spf', 'sun protection'], 'sunscreen lotion bottle'],
  [['lip balm', 'lip butter', 'lip gloss'],    'lip balm cosmetics'],
  [['deodorant', 'antiperspirant'],            'deodorant stick bathroom'],
  [['toothpaste', 'tooth paste'],              'toothpaste bathroom'],
  [['mouthwash'],                              'mouthwash bathroom bottle'],
  [['bar soap', 'hand soap', 'soap'],          'soap bar bathroom'],
  [['body lotion', 'body butter', 'body cream'], 'body lotion bottle'],
  [['perfume', 'fragrance', 'cologne'],        'perfume bottle'],
  [['foundation', 'concealer', 'blush', 'mascara', 'makeup'], 'makeup cosmetics'],
  [['face mask', 'sheet mask'],                'face mask skincare'],
  [['exfoliant', 'exfoliator', 'face scrub'], 'face scrub skincare'],
  [['retinol', 'vitamin c serum', 'hyaluronic'], 'skincare serum bottle dropper'],

  // ── Baby Care ───────────────────────────────────────────────────────────────
  [['baby shampoo', 'baby wash', 'baby bath'],       'baby bath products'],
  [['baby lotion', 'baby cream', 'baby moisturizer'], 'baby skincare lotion nursery'],
  [['baby powder'],                                   'baby powder nursery'],
  [['baby oil'],                                      'baby oil nursery'],
  [['baby wipes', 'diaper wipes'],                   'baby wipes nursery'],
  [['diaper rash', 'diaper cream'],                  'baby diaper cream nursery'],
  [['baby sunscreen'],                               'baby sunscreen nursery'],

  // ── Pet Care ────────────────────────────────────────────────────────────────
  [['puppy', 'dog shampoo', 'dog wash', 'dog bath'],  'dog bath grooming'],
  [['kitten', 'cat shampoo', 'cat wash', 'cat grooming'], 'cat grooming'],
  [['flea', 'tick'],                                   'dog outdoor grooming'],
  [['pet shampoo', 'pet wash', 'pet conditioner'],    'pet dog grooming bath'],

  // ── Home Cleaning ───────────────────────────────────────────────────────────
  [['laundry detergent', 'laundry soap', 'washing powder', 'washing liquid'], 'laundry detergent bottle'],
  [['fabric softener', 'dryer sheet'],                'laundry fabric softener'],
  [['bathroom cleaner', 'toilet cleaner', 'tub cleaner', 'shower cleaner'], 'bathroom cleaning spray'],
  [['glass cleaner', 'window cleaner'],               'glass cleaning spray bottle'],
  [['floor cleaner', 'mop solution'],                 'floor cleaning mop'],
  [['disinfectant', 'sanitizer'],                     'disinfectant cleaning spray'],
  [['all-purpose', 'all purpose', 'multi-surface'],   'all purpose cleaning spray'],

  // ── Kitchen — always cleaning items, never food ─────────────────────────────
  [['dish soap', 'dishwashing liquid', 'dish detergent'], 'dish soap bottle kitchen sink'],
  [['dishwasher'],                                        'dishwasher cleaning tablet'],
  [['sponge', 'scrubber', 'scrub pad'],                  'kitchen sponge sink'],
  [['grease', 'degreaser'],                              'kitchen cleaning degreaser'],
  [['countertop', 'counter spray', 'kitchen spray'],     'kitchen counter cleaning spray'],
];

// Curated clean-eating / nutritious lifestyle photos for Food & Pantry.
// Hand-picked from Pexels across: grain bowls, smoothies, natural grocery,
// meal prep, and whole foods. No API call needed — hardcoded for stability.
const FOOD_PANTRY_PHOTOS = [
  'https://images.pexels.com/photos/1105166/pexels-photo-1105166.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // colorful grain bowl
  'https://images.pexels.com/photos/6740849/pexels-photo-6740849.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // clean eating bowl
  'https://images.pexels.com/photos/9213852/pexels-photo-9213852.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // nourish bowl
  'https://images.pexels.com/photos/6978146/pexels-photo-6978146.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // healthy bowl overhead
  'https://images.pexels.com/photos/4443507/pexels-photo-4443507.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // wellness smoothie
  'https://images.pexels.com/photos/4552049/pexels-photo-4552049.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // green smoothie
  'https://images.pexels.com/photos/1438082/pexels-photo-1438082.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // smoothie / juice
  'https://images.pexels.com/photos/7456557/pexels-photo-7456557.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // natural grocery bag
  'https://images.pexels.com/photos/4451868/pexels-photo-4451868.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // wholesome market
  'https://images.pexels.com/photos/1640771/pexels-photo-1640771.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // meal prep containers
  'https://images.pexels.com/photos/9050064/pexels-photo-9050064.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // healthy meal prep
  'https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // organic whole foods
  'https://images.pexels.com/photos/3847450/pexels-photo-3847450.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // natural pantry items
  'https://images.pexels.com/photos/2101261/pexels-photo-2101261.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // organic products
  'https://images.pexels.com/photos/8287244/pexels-photo-8287244.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',   // whole foods lifestyle
];

function deriveQuery(product) {
  // Food & Pantry → hardcoded photos, no Pexels query needed
  if (product.category === 'Food & Pantry') return null;

  const name = product.name.toLowerCase();

  for (const [keywords, query] of TYPE_QUERIES) {
    if (keywords.some(kw => name.includes(kw))) return query;
  }

  // Category fallbacks — specific enough to avoid food results
  const FALLBACKS = {
    'Personal Care':  'skincare bottle bathroom shelf',
    'Home Cleaning':  'cleaning spray bottle supplies',
    'Baby Care':      'baby nursery soft products',
    'Kitchen':        'dish soap kitchen sink cleaning',
    'Pet Care':       'dog cat pet grooming',
  };
  return FALLBACKS[product.category] || 'skincare bottle bathroom';
}

// ─── Pexels fetch with deduplication + session caching ────────────────────────
// Keyed by query string → Promise<string[]> (resolved photo URLs).
// Concurrent calls for the same query share one in-flight request.
const _cache = new Map();

async function fetchPhotos(query) {
  if (_cache.has(query)) return _cache.get(query);
  const p = fetch(`/api/pexels-image?q=${encodeURIComponent(query)}`)
    .then(r => (r.ok ? r.json() : { photos: [] }))
    .then(d => Array.isArray(d.photos) ? d.photos : [])
    .catch(() => []);
  _cache.set(query, p);
  return p;
}

// djb2 hash — deterministic per product name
function hashCode(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h * 33 ^ str.charCodeAt(i)) >>> 0;
  return h;
}

// ─── Fallback tile ─────────────────────────────────────────────────────────────
const FALLBACK_STYLES = {
  clean:   { bg: 'bg-primary-50', color: 'text-primary-400'  },
  caution: { bg: 'bg-[#FFFBEB]',  color: 'text-warning'      },
  avoid:   { bg: 'bg-[#FEF2F2]',  color: 'text-error'        },
};

const CATEGORY_ICONS = {
  'Personal Care': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2C8 6 8 14 12 22c4-8 4-16 0-20z"/><path d="M8 13l4 3 4-3"/>
    </svg>
  ),
  'Home Cleaning': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.5 14.5 3 21"/><path d="M14 6l-8.5 8.5a2.12 2.12 0 0 0 3 3L17 9z"/><path d="m14 6 3-3 3 3-3 3z"/>
    </svg>
  ),
  'Baby Care': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
    </svg>
  ),
  'Kitchen': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>
    </svg>
  ),
  'Pet Care': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2.823.47-4.113 6.006-4 7 .08.703 1.725 1.722 3.656 1 1.261-.472 1.96-1.45 2.344-2.5"/>
      <path d="M14.267 5.172c0-1.39 1.577-2.493 3.5-2.172 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"/>
      <path d="M8 14v.5A3.5 3.5 0 0 0 11.5 18h1a3.5 3.5 0 0 0 3.5-3.5V14a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2z"/>
      <path d="M9 18.5 8 22l4-1.5 4 1.5-1-3.5"/>
    </svg>
  ),
  'Food & Pantry': (
    <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
};

function FallbackTile({ product }) {
  const style = FALLBACK_STYLES[product.safetyRating] ?? FALLBACK_STYLES.caution;
  const icon  = CATEGORY_ICONS[product.category];
  return (
    <div className={`w-full h-48 flex-shrink-0 flex flex-col items-center justify-center gap-2 ${style.bg}`}>
      <span className={style.color}>{icon}</span>
      <span className={`text-caption font-semibold select-none ${style.color} opacity-50`}>
        {product.category}
      </span>
    </div>
  );
}

// ─── ProductImage ──────────────────────────────────────────────────────────────
function ProductImage({ product }) {
  const query = deriveQuery(product);

  // Food & Pantry → pick from hardcoded pool by hash, no API call
  const foodPantryUrl = product.category === 'Food & Pantry'
    ? FOOD_PANTRY_PHOTOS[hashCode(product.name) % FOOD_PANTRY_PHOTOS.length]
    : null;

  const [url, setUrl]       = useState(foodPantryUrl ?? (query ? undefined : null));
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (!query || foodPantryUrl) return;
    let cancelled = false;
    fetchPhotos(query).then(photos => {
      if (cancelled) return;
      if (photos.length === 0) { setUrl(null); return; }
      const idx = hashCode(product.name) % photos.length;
      setUrl(photos[idx]);
    });
    return () => { cancelled = true; };
  }, [query, product.name, foodPantryUrl]);

  // Loading state — neutral shimmer matching tile height
  if (url === undefined) {
    return <div className="w-full h-48 flex-shrink-0 bg-neutral-100 animate-pulse" />;
  }

  // Fallback tile — no URL, or img failed to load, or Food & Pantry
  if (url === null || imgFailed) {
    return <FallbackTile product={product} />;
  }

  return (
    <div className="w-full h-48 flex-shrink-0 overflow-hidden">
      <img
        src={url}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setImgFailed(true)}
      />
    </div>
  );
}

// ─── ProductCard ───────────────────────────────────────────────────────────────
function ProductCard({ product, onSave, onClick, saved = false }) {
  function handleSave(e) {
    e.stopPropagation();
    if (onSave) onSave(product);
  }

  return (
    <article
      onClick={onClick}
      className={`bg-surface-card rounded-lg shadow-sm border border-neutral-200 flex flex-col overflow-hidden transition-shadow duration-fast ${onClick ? 'cursor-pointer hover:shadow-md' : ''}`}
    >
      <ProductImage product={product} />

      <div className="p-6 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            {product.brand && (
              <p className="text-small text-neutral-500">{product.brand}</p>
            )}
            <h3 className="text-h4 font-semibold text-neutral-900">{product.name}</h3>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <SafetyBadge rating={product.safetyRating} size="md" />
            {product.safetyScore != null && (
              <span className="text-caption text-neutral-400">Score: {product.safetyScore}</span>
            )}
          </div>
        </div>

        <div className="self-start">
          <CategoryTag label={product.category} />
        </div>

        <p className="text-body text-neutral-600 line-clamp-2">{product.description}</p>

        <div className="flex items-center gap-3 mt-auto pt-2">
          <button
            type="button"
            onClick={handleSave}
            className={`px-4 py-2 text-small font-semibold rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              saved ? 'text-primary-600 hover:bg-primary-50' : 'text-primary-500 hover:bg-primary-50'
            }`}
          >
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>
    </article>
  );
}

export default ProductCard;
